// ---------------------------------------------------------------------------
// Tipos do motor de deteccao de conflitos (clash detection).
//
// Vocabulario (identificadores em EN, por convencao VN2R; a UI traduz):
//
//   hard       interpenetracao real de geometria: os solidos ocupam o
//              mesmo espaco. Tocar nao conta (ver `tolerance`).
//   clearance  os solidos NAO se tocam mas a distancia minima entre
//              eles e menor ou igual a `clearance` metros (folga
//              regulamentar, espaco de manutencao, isolamento).
//   tolerance  penetracao minima, em metros, para um conflito hard
//              contar. Abaixo disto e contacto/ruido de tesselacao e e
//              descartado. Nao e a tolerancia numerica do tri-tri
//              (essa e derivada da grandeza das coordenadas).
//   depth      profundidade de penetracao APROXIMADA, em metros. Serve
//              para ORDENAR por gravidade, nao para medir. Ver
//              docs/CLASH.md, seccao "O que e exacto e o que e
//              aproximado".
//   distance   distancia minima entre as duas geometrias, em metros.
//              EXACTA ao nivel dos triangulos (minimo entre malhas
//              tesseladas).
//
// Todas as distancias em metros. Todos os pontos e caixas devolvidos
// pelo orquestrador estao em coordenadas de MUNDO.
// ---------------------------------------------------------------------------

/** Caixa alinhada aos eixos, em metros. */
export interface ClashBox {
  min: [number, number, number]
  max: [number, number, number]
}

/** Par de elementos em conflito. `modelA`/`modelB` sao os `modelId` do
 *  Fragments; `localIdA`/`localIdB` os ids locais dentro de cada modelo. */
export interface ClashPair {
  modelA: string
  localIdA: number
  modelB: string
  localIdB: number
}

export type ClashKind = 'hard' | 'clearance'

/** Como o conflito hard foi apurado. Interessa a UI para nao mentir ao
 *  utilizador sobre o que foi medido:
 *   - `surface`: ha triangulos a atravessarem-se (caso normal).
 *   - `containment`: nenhum triangulo se cruza, mas um elemento esta
 *     inteiramente dentro do outro (ex.: uma conduta embebida num
 *     muro). Detectado por paridade de raio; ver docs/CLASH.md. */
export type ClashEvidence = 'surface' | 'containment'

export interface ClashResult {
  pair: ClashPair
  kind: ClashKind
  /** Categoria IFC de cada elemento, quando conhecida. Vem preenchida
   *  para a UI nao ter de voltar a perguntar ao modelo. */
  categoryA?: string | null
  categoryB?: string | null
  /** Profundidade APROXIMADA de penetracao, em metros. So em `hard`. */
  depth?: number
  /** Distancia minima entre as malhas, em metros. So em `clearance`. */
  distance?: number
  /** Ponto representativo em coordenadas de MUNDO: serve para apontar a
   *  camara e para o viewpoint do BCF. Em `hard` e o centro da zona de
   *  interseccao; em `clearance` e o ponto medio do segmento mais
   *  curto entre as duas geometrias. */
  point: [number, number, number]
  /** Caixa de MUNDO da zona de interesse (interseccao ou vao). */
  bbox: ClashBox
  /** Numero de pares de triangulos que se cruzaram (0 em containment e
   *  em clearance). Diagnostico, nao gravidade. */
  triangles: number
  /** Como o hard foi apurado. Ausente em clearance. */
  evidence?: ClashEvidence
  /** True quando o teste do par bateu no tecto de pares de triangulos
   *  e parou cedo: o resultado e valido mas `depth` pode estar
   *  subestimado. */
  truncated?: boolean
}

/** Filtro por categoria IFC. Comparacao sem distinguir maiusculas.
 *
 *  `include`/`exclude` filtram QUE elementos entram na analise.
 *  `setA`/`setB` definem uma MATRIZ: so se testam pares com um elemento
 *  de A contra um elemento de B (o classico "Estrutura vs AVAC"). Se so
 *  um dos dois vier preenchido e ignorado. */
export interface ClashCategoryFilter {
  include?: string[]
  exclude?: string[]
  setA?: string[]
  setB?: string[]
}

export interface ClashConfig {
  /** Penetracao minima para um hard contar, em metros. */
  tolerance: number
  /** Distancia de folga a verificar, em metros. 0 desliga. */
  clearance: number
  /** Testar elementos do MESMO modelo uns contra os outros. */
  selfClash: boolean
  categoryFilter?: ClashCategoryFilter | null
  /** Tecto de pares candidatos saidos da broad-phase. */
  maxPairs: number
  /** Elementos com mais triangulos do que isto sao saltados (contam em
   *  `stats.skippedHeavy`). Protege a memoria e o tempo. */
  maxTrianglesPerElement: number
}

export const DEFAULT_CLASH_CONFIG: ClashConfig = {
  tolerance: 0.001,
  clearance: 0,
  selfClash: false,
  categoryFilter: null,
  maxPairs: 200_000,
  maxTrianglesPerElement: 20_000,
}

/** Tecto de pares de triangulos testados por par de elementos. Acima
 *  disto o par e dado como truncado (resultado valido, `depth` possivelmente
 *  subestimado). Dois elementos com 20 mil triangulos cada nunca chegam
 *  perto disto depois do pre-filtro pela zona de sobreposicao. */
export const MAX_TRIANGLE_PAIRS_PER_ELEMENT_PAIR = 4_000_000

/** Preenche os campos em falta com os valores por omissao e sanea os
 *  numeros (negativos, NaN). Nunca lanca. */
export function normalizeClashConfig(
  cfg?: Partial<ClashConfig> | null,
): ClashConfig {
  const d = DEFAULT_CLASH_CONFIG
  const num = (v: unknown, fallback: number, min = 0): number => {
    const n = typeof v === 'number' && Number.isFinite(v) ? v : fallback
    return n < min ? min : n
  }
  return {
    tolerance: num(cfg?.tolerance, d.tolerance),
    clearance: num(cfg?.clearance, d.clearance),
    selfClash: cfg?.selfClash === true,
    categoryFilter: cfg?.categoryFilter ?? null,
    maxPairs: Math.max(1, Math.floor(num(cfg?.maxPairs, d.maxPairs, 1))),
    maxTrianglesPerElement: Math.max(
      1,
      Math.floor(num(cfg?.maxTrianglesPerElement, d.maxTrianglesPerElement, 1)),
    ),
  }
}

/** Elemento a analisar, ja com a caixa de MUNDO resolvida. */
export interface ClashElementInput {
  modelId: string
  localId: number
  category?: string | null
  box: ClashBox
}

export interface ClashStats {
  /** Elementos que entraram na analise (depois dos filtros). */
  elements: number
  /** Elementos deitados fora por caixa corrompida ou nao finita. */
  skippedBoxes: number
  /** Elementos saltados por excederem `maxTrianglesPerElement`. */
  skippedHeavy: number
  /** Pares candidatos devolvidos pela broad-phase. */
  candidatePairs: number
  /** Pares efectivamente levados a narrow-phase. */
  testedPairs: number
  hard: number
  clearance: number
  /** A broad-phase bateu no `maxPairs`. */
  truncatedPairs: boolean
  /** Milissegundos por fase. */
  ms: { total: number; collect: number; broad: number; narrow: number }
  /** Tamanho da celula da grelha da broad-phase, em metros. */
  cellSize: number
  /** A corrida foi cancelada a meio (resultados parciais). */
  cancelled: boolean
}

export interface ClashRun {
  results: ClashResult[]
  stats: ClashStats
  config: ClashConfig
}

export type ClashPhase = 'collect' | 'broad' | 'narrow' | 'done'

export interface ClashProgress {
  phase: ClashPhase
  /** Unidades feitas e totais DENTRO da fase (elementos, pares). */
  done: number
  total: number
  /** Conflitos ja encontrados. */
  results: number
}

// ---------------------------------------------------------------------------
// Protocolo do motor (main thread <-> worker). Tudo estruturado para
// clonagem/transferencia: nada de funcoes, nada de classes.
// ---------------------------------------------------------------------------

export interface BroadRequest {
  /** 6 numeros por elemento: minx,miny,minz,maxx,maxy,maxz. */
  boxes: Float64Array
  /** Indice do modelo de cada elemento (para o `selfClash`). */
  modelIndex: Uint32Array
  /** Indice na tabela `categories`, -1 = sem categoria. */
  categoryIndex: Int32Array
  /** Catalogo de categorias, ja em maiusculas. */
  categories: string[]
  config: ClashConfig
}

export interface BroadResponse {
  /** 2 indices de elemento por par, ordenados (a < b) e o array
   *  ordenado por (a, b). */
  pairs: Uint32Array
  /** Pares que passaram pela grelha e foram testados por caixa. */
  tested: number
  truncated: boolean
  cellSize: number
  cells: number
  ms: number
}

/** Geometria de um elemento dentro de um lote da narrow-phase. As
 *  posicoes sao RELATIVAS a `origin` do lote (precisao em modelos
 *  georreferenciados). */
export interface NarrowElement {
  /** Indice global do elemento (o mesmo que a broad-phase usa). */
  index: number
  positions: Float32Array
  indices: Uint32Array
}

export interface NarrowRequest {
  origin: [number, number, number]
  config: ClashConfig
  /** 2 indices GLOBAIS de elemento por par. */
  pairs: Uint32Array
  elements: NarrowElement[]
}

/** Resultado cru de um par, ainda no referencial de `origin`. */
export interface NarrowPairResult {
  a: number
  b: number
  kind: ClashKind
  depth?: number
  distance?: number
  point: [number, number, number]
  bbox: ClashBox
  triangles: number
  evidence?: ClashEvidence
  truncated?: boolean
}

export interface NarrowResponse {
  results: NarrowPairResult[]
  testedPairs: number
  /** Pares saltados por falta de geometria no lote. */
  missing: number
  ms: number
}

/** Motor: local (mesma thread) ou worker. O orquestrador so conhece
 *  esta interface. */
export interface ClashEngine {
  broadPhase(req: BroadRequest): Promise<BroadResponse>
  narrowPhase(req: NarrowRequest): Promise<NarrowResponse>
  dispose(): void
}
